"use client";

import React, { FC, useEffect, useState } from "react";
import { find } from "lodash";
import { CgSpinner } from "react-icons/cg";
import { Loading } from "@/components/Loading";
import { PricingLoggedContainer } from "./PricingLoggedContainer/PricingLoggedContainer";
import { PricingContainer } from "./PricingContainer/PricingContainer";
import { PlansManager } from "./PlansManager";

import { UserContext } from "@/contexts/UserContext";
import { AmplitudeContext } from "@/contexts/AmplitudeContext/AmplitudeContext";

import { SubscriptionPlan } from "@/types/types";
import { ISubscriptionPlansContainer } from "./types";
import { PlanProps } from "@/types/plan";
import { isSubscriptionActive, isUserSubscribed } from "@/services/UserService";
import { amplitudeTrackEvent } from "@/services/AmplitudeService/AmplitudeService";
import { validatePricngEventSource } from "@/utils/qrEditorNavigatorHelper";

const mergePlansWithData = (
  plansList: PlanProps[],
  plansInfo: SubscriptionPlan[]
): SubscriptionPlan[] => {
  return plansInfo.map((plan) => {
    const pricingPlan = find(plansList, (currentPlan) => currentPlan.id === plan.id);
    const completedPlan = {
      ...plan,
      data: {
        ...pricingPlan,
        price: pricingPlan?.price
      },
    };
    return completedPlan;
  });
};

export const SubscriptionPlansContainer: FC<ISubscriptionPlansContainer> = ({ data, plans }) => {
  const { containerComponent } = data;
  const userContext = React.useContext(UserContext);
  const { source, qrSelected } = React.useContext(AmplitudeContext);

  const plansInfo = data?.prices;

  const pricingPlans = mergePlansWithData(plans, plansInfo);

  const [currentPlan, setCurrentPlan] = useState<SubscriptionPlan>(
    find(pricingPlans, (plan) => plan.selected)
  );
  const [isMounted, setIsMounted] = useState(false);

  const onSelectPlan = (planId: string) => {
    const selectedPlan = find(pricingPlans, (plan) => plan.id === planId);
    setCurrentPlan(selectedPlan);
  };

  const isStatusActive = isUserSubscribed(userContext) && isSubscriptionActive(userContext);

  useEffect(() => {
    setIsMounted(true);
    if (userContext) {
      userContext.setPrices(plans);
    }
    const sourceEv = { source: validatePricngEventSource(source) };

    amplitudeTrackEvent("view_pricing", {
      ...(validatePricngEventSource(source) && sourceEv),
      ...(qrSelected && { "qr type": qrSelected }),
    });
  }, []);

  return (
    <main>
      <PlansManager
        userContext={userContext}
        data={data}
        currentPlan={currentPlan}
      />
      {!isMounted && data.containerComponent === "PricingLoggedContainer" ? (
        <div className="subscription-plans-loader__container">
          <Loading
            dataQA="loader"
            className="loader pricing"
            icon={<CgSpinner color="#ffaf76" />}
          />
        </div>
      ) : (
        <>
          {containerComponent === "PricingLoggedContainer" ? (
            <PricingLoggedContainer
              data={data}
              currentPlan={currentPlan}
              onSelectPlan={onSelectPlan}
              isStatusActive={isStatusActive}
            />
          ) : (
            <PricingContainer
              data={data}
              currentPlan={currentPlan}
              onSelectPlan={onSelectPlan}
              isStatusActive={isStatusActive}
            />
          )}
        </>
      )}
    </main>
  );
};
